import { type Table } from '@tanstack/react-table'
import React from 'react'

import type { VirtualizerType } from '../../Virtualizer/Virtualizer'
import { DataTableCell } from '../DataTableCell/DataTableCell'
import { TableCell } from '../DataTableCell/DataTableCell.styles'
import { TableRow } from '../DataTableCore.styles'
import { DataTableVirtualBody } from './DataTableVirtualBody'

export interface DataTableBodyProps<TData> {
  rowHeight: number
  table: Table<TData>
  rowVirtualizer?: VirtualizerType
  withBorder?: boolean
}

export const DataTableBody = <TData,>({
  rowHeight,
  rowVirtualizer,
  table,
  withBorder,
}: DataTableBodyProps<TData>) => {
  const { rows } = table.getRowModel()

  if (rowVirtualizer) {
    return (
      <DataTableVirtualBody
        rowHeight={rowHeight}
        rowVirtualizer={rowVirtualizer}
        table={table}
        withBorder={withBorder}
      />
    )
  }

  return (
    <tbody>
      {rows.map(row => (
        <TableRow
          $rowHeight={rowHeight}
          data-e2e="data-table-row"
          data-index={row.index}
          data-row-disabled={(row.original as TData & { disabled?: boolean }).disabled ?? false}
          key={row.id}
        >
          {row.getVisibleCells().map(cell => (
            <DataTableCell
              cell={cell}
              isDisabled={(row.original as TData & { disabled?: boolean }).disabled}
              key={cell.id}
              rowHeight={rowHeight}
              withBorder={withBorder}
            />
          ))}

          <TableCell columnId="last-column" $rowHeight={rowHeight} />
        </TableRow>
      ))}
    </tbody>
  )
}

// Special memoized wrapper for the table body that will be used during column resizing
export const MemoizedDataTableBody = React.memo(
  DataTableBody,
  (prev, next) => prev.table.options.data === next.table.options.data
) as typeof DataTableBody
